/* eslint frontbucket-patterns/no-new-emotion-styled: "warn" */
import styled from '@emotion/styled';

import { layers } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';

const TRANSITION_DURATION_BASE = 0.2;
const TRANSITION_DURATION = `${TRANSITION_DURATION_BASE}s`;

export const SidebarContainer = styled.section({
  overflow: 'hidden',
  width: '100%',
});

export const Sidebar = styled.div<{
  shouldAnimate: boolean;
  backgroundColor?: string;
}>(
  {
    display: 'flex',
    minWidth: '64px',
    height: '100%',
    width: '100%',
  },
  ({ shouldAnimate, backgroundColor }) => ({
    transition: shouldAnimate
      ? `all ${TRANSITION_DURATION} ease-out;`
      : undefined,
    backgroundColor,
  }),
  {
    outline: 'none',
  }
);

export const SplitBar = styled.div({
  height: '100%',
  width: '100%',
  cursor: 'ew-resize',
  position: 'absolute',
  '&::after': {
    content: "''",
    position: 'absolute',
    left: '5px',
    top: 0,
    display: 'table',
    width: '2px',
    height: '100%',
  },
  '&:hover::after, &:focus::after': {
    backgroundColor: token('color.background.information.hovered'),
  },
  '&:active::after': {
    backgroundColor: token('color.background.information.hovered'),
  },
});

export const ExpandedSidebar = styled.div<{
  isVisible: boolean;
  contentOnly?: boolean;
}>(({ isVisible, contentOnly }) => ({
  boxSizing: 'border-box',
  display: isVisible ? 'flex' : 'none',
  flexDirection: 'column',
  padding: contentOnly ? undefined : `0 ${token('space.150')}`,
}));

export const CollapsedSidebar = styled.div<{
  isVisible: boolean;
  contentOnly?: boolean;
}>(({ isVisible, contentOnly }) => ({
  boxSizing: 'border-box',
  display: isVisible ? 'flex' : 'none',
  flexDirection: 'column',
  padding: contentOnly ? undefined : token('space.150'),
}));

export const SidebarContent = styled.div<{
  contentOnly?: boolean;
  hasOneChildElement?: boolean;
}>(({ contentOnly, hasOneChildElement }) => ({
  overflowY: 'scroll',
  marginRight: contentOnly ? undefined : '-17px',
  paddingRight: contentOnly ? undefined : '17px',
  display: hasOneChildElement ? 'flex' : undefined,
  '&::after': {
    content: "''",
    display: 'block',
    height: '64px',
  },
}));

export const ScrollToAnchor = styled.div({
  position: 'relative',
  top: 0,
  left: token('space.negative.150'),
});

export const SidebarChild = styled.div({
  marginTop: token('space.150'),
  textAlign: 'center',
  '&:empty': {
    marginTop: 0,
  },
});

// The z-index here is set to 100 to be greater
// than the number of cards in the sidebar.
// see: src/components/sidebar/src/utils/wrap-child.js
export const SidebarControls = styled.div({
  position: 'absolute',
  left: token('space.negative.100'),
  top: 0,
  height: '100%',
  width: '12px',
  userSelect: 'none',
  zIndex: layers.card(),
});

/* stylelint-disable a11y/media-prefers-reduced-motion */
export const Arrow = styled.button<{ isCollapsed: boolean }>`
  background: none;
  border: none;
  opacity: 0;
  position: absolute;
  top: 50%;
  width: 16px;
  left: ${token('space.negative.150')};
  padding-right: ${token('space.100')};
  cursor: pointer;

  &::-moz-focus-inner {
    border: 0;
  }

  &:focus {
    box-shadow: 0 0 0 2px ${token('color.text.information')};
    outline: none;
  }

  /* In order to get rid of explicit definition of
   classnames ::before and ::after pseudo elements are used. */
  &::before,
  &::after {
    content: '';
    display: block;
    position: relative;
    margin-right: 0;
    margin-left: auto;
    width: 2px;
    height: 8px;
    background-color: ${token('color.text.information')};
    border-radius: 5px;
    transition: all 0.15s ease-out;
    transform: rotate(0deg);
  }

  &::before {
    top: 2px;
    transform-origin: 1px 6px;
  }

  &::after {
    top: 0;
    transform-origin: 1px 2px;
  }

  &:hover,
  &:focus {
    opacity: 1;

    &::before {
      /* prettier-ignore */
      transform:
        rotate(
          ${({ isCollapsed }) => (isCollapsed ? '45deg' : '-45deg')}
        );
    }

    &::after {
      /* prettier-ignore */
      transform:
        rotate(
          ${({ isCollapsed }) => (isCollapsed ? '-45deg' : '45deg')}
        );
    }
  }

  /* stylelint-disable selector-type-no-unknown, a11y/selector-pseudo-class-focus */
  ${SidebarControls}:hover & {
    opacity: 1;
    transition: all ${TRANSITION_DURATION} ease-out;
  }
  /* stylelint-enable */
`;
/* stylelint-enable */
